import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const STATUS_BADGE = {
  draft:     'bg-gray-100 text-gray-700',
  published: 'bg-emerald-100 text-emerald-700',
  archived:  'bg-amber-100 text-amber-700',
};

function formatPrice(price) {
  if (price == null) return null;
  if (Number(price) === 0) return 'Included';
  return `$${Number(price).toFixed(2)}`;
}

function formatDuration(minutes) {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem ? `${hours}h ${rem}m` : `${hours}h`;
}

function BetaBanner() {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      <div className="flex gap-3">
        <svg className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
        <div>
          <p className="font-semibold">Beta — work in progress</p>
          <p className="mt-1 text-amber-800">
            This is the native course catalog we&apos;re building to replace LearnWorlds.
            It&apos;s superadmin-only while data is being migrated. Run{' '}
            <code className="px-1 py-0.5 rounded bg-amber-100 text-amber-900 text-xs font-mono">
              bin/rails learn_worlds:import_courses
            </code>{' '}
            to refresh.
          </p>
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course }) {
  const price = formatPrice(course.price);
  const duration = formatDuration(course.duration_minutes);
  const badgeClass = STATUS_BADGE[course.status] || 'bg-gray-100 text-gray-700';
  return (
    <Link
      href={`/superadmin/courses/${course.slug}`}
      className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-indigo-300 hover:shadow-md transition flex flex-col"
    >
      <div className="aspect-video bg-gray-100 overflow-hidden">
        {course.image_url ? (
          <img
            src={course.image_url}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
            </svg>
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-indigo-700">
            {course.title}
          </h3>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${badgeClass}`}>
            {course.status}
          </span>
        </div>
        {course.summary && (
          <p className="mt-2 text-sm text-gray-600 line-clamp-3">{course.summary}</p>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
          {course.ceu_credit_hours != null && (
            <span className="inline-flex items-center gap-1">
              <span className="font-medium text-gray-700">{course.ceu_credit_hours}</span> CEU
            </span>
          )}
          {duration && (
            <>
              <span className="text-gray-300">•</span>
              <span>{duration}</span>
            </>
          )}
          {course.learn_worlds_id && (
            <>
              <span className="text-gray-300">•</span>
              <span className="font-mono text-[10px] text-gray-400">{course.learn_worlds_id}</span>
            </>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">
            {price ?? <span className="text-gray-400 font-normal">—</span>}
          </span>
          <span className="text-sm font-medium text-indigo-600 group-hover:text-indigo-700">
            View →
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function SuperadminCoursesIndex({ courses = [], filters = {}, facets = {}, total_count = 0 }) {
  const [query, setQuery] = useState(filters.q || '');
  const statuses = facets.statuses || [];

  function submit(params) {
    router.get('/superadmin/courses', params, { preserveState: true, preserveScroll: true });
  }

  function onSubmit(e) {
    e.preventDefault();
    submit({ q: query, status: filters.status });
  }

  function onClear() {
    setQuery('');
    submit({});
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <BetaBanner />

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Courses</h1>
            <p className="text-sm text-gray-500 mt-1">
              Local mirror of the LearnWorlds catalog.
            </p>
          </div>
          <div className="text-sm text-gray-500">
            {total_count} {total_count === 1 ? 'course' : 'courses'} stored
          </div>
        </div>

        <form onSubmit={onSubmit} className="flex flex-wrap gap-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title or slug…"
            className="flex-1 min-w-[200px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
          <select
            value={filters.status || ''}
            onChange={(e) => submit({ q: query, status: e.target.value || undefined })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">All statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            type="submit"
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Search
          </button>
          {(filters.q || filters.status) && (
            <button
              type="button"
              onClick={onClear}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </form>

        {courses.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center">
            <p className="text-gray-700 font-medium">No courses yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Run{' '}
              <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-800 text-xs font-mono">
                bin/rails learn_worlds:import_courses
              </code>{' '}
              to populate from LearnWorlds.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
